import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ModalContainer, ModalContent, Button, Textarea, Input } from './StyledComponents';
import { submitBugReport } from '../services/bugReportService';

const BugReportModal = ({ isOpen, onClose, context }) => {
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSummary('');
      setDetails('');
      setIsSubmitting(false);
      setSubmitError(null);
      setSubmitSuccess(false);
    }
  }, [isOpen]);

  const gatherEnvironmentDetails = () => {
    const info = {};

    if (typeof navigator !== 'undefined') {
      info.userAgent = navigator.userAgent;
      if ('onLine' in navigator) {
        info.online = navigator.onLine;
      }
      if (navigator.language) {
        info.language = navigator.language;
      }
    }

    if (typeof window !== 'undefined') {
      info.location = window.location?.href;
    }

    return info;
  };

  const handleSubmit = async () => {
    if (!summary.trim() && !details.trim()) {
      setSubmitError('Please add a summary or details before sending.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitBugReport({
        summary: summary.trim(),
        details: details.trim(),
        includeContext: true,
        contextData: context,
        environment: gatherEnvironmentDetails(),
      });

      setSubmitSuccess(true);

      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (error) {
      console.error('Failed to send bug report', error);
      const message = error?.message || 'Failed to send bug report. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalContainer onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <h3>Report a Bug</h3>
        <p>Provide as much detail as you can. We&apos;ll save the report so the support team can review it.</p>

        <label style={{ display: 'block', marginBottom: '8px' }}>
          Summary
          <Input
            type="text"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Brief summary"
            style={{ marginTop: '4px' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '8px' }}>
          Details
          <Textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Steps to reproduce, expected vs actual results"
            style={{ marginTop: '4px', minHeight: '120px' }}
          />
        </label>

        {submitError && (
          <div style={{ color: '#d32f2f', fontSize: '0.9em', marginBottom: '8px' }}>
            {submitError}
          </div>
        )}
        {submitSuccess && (
          <div style={{ color: '#2e7d32', fontSize: '0.9em', marginBottom: '8px' }}>
            Bug report saved. Thank you!
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Send Report'}
          </Button>
          <Button onClick={onClose} disabled={isSubmitting}>
            Close
          </Button>
        </div>
      </ModalContent>
    </ModalContainer>
  );
};

BugReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  context: PropTypes.object,
};

BugReportModal.defaultProps = {
  context: null,
};

export default BugReportModal;
